/* AYU Gesundheitstagebuch Section – Analog zu MENTO Tagebuch
 * Light section with animated phone mockup (left) and feature list (right)
 * Phone shows a diary-style conversation with AYU Reflexion
 * Features: Reflektieren, Verstehen, Dokumentieren, Wachsen
 */
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { PenLine, MessageSquare, CalendarDays, TrendingUp } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useI18n } from "@/contexts/I18nContext";

const diaryFeatures = [
  { icon: PenLine, titleKey: "landing.tagebuch.feature1Title", descriptionKey: "landing.tagebuch.feature1Desc" },
  { icon: MessageSquare, titleKey: "landing.tagebuch.feature2Title", descriptionKey: "landing.tagebuch.feature2Desc" },
  { icon: CalendarDays, titleKey: "landing.tagebuch.feature3Title", descriptionKey: "landing.tagebuch.feature3Desc" },
  { icon: TrendingUp, titleKey: "landing.tagebuch.feature4Title", descriptionKey: "landing.tagebuch.feature4Desc" },
];

type DiaryMessage = {
  type: "user" | "bot" | "date" | "typing" | "label";
  text?: string;
  label?: string;
  sublabel?: string;
};

const diarySequence: DiaryMessage[] = [
  { type: "date", text: "Donnerstag, 6. März", sublabel: "Morgen-Check-in" },
  {
    type: "user",
    text: "Ich habe seit drei Tagen Kopfschmerzen und fühle mich aufgebläht. Gestern Abend konnte ich kaum schlafen.",
  },
  {
    type: "bot",
    label: "AYU REFLEXION",
    text: "Das klingt, als ob dein Körper dir etwas sagen möchte. Kopfschmerzen und Blähungen zusammen deuten oft auf ein Ungleichgewicht im Verdauungsfeuer hin. Hast du in den letzten Tagen etwas Kaltes oder Schweres gegessen?",
  },
  {
    type: "user",
    text: "Ja, ich hatte viel Salat und kalte Getränke, weil ich dachte, das wäre gesund.",
  },
  { type: "label", text: "Dein Eintrag" },
  {
    type: "bot",
    label: "MUSTER ERKANNT",
    text: "Für deinen Phänotyp sind kalte, rohe Speisen besonders herausfordernd. Dein Agni braucht Wärme. Das ist kein Fehler — es ist ein Lernmoment. Versuche heute: warme Suppe, Ingwertee, und eine Wärmflasche auf dem Bauch.",
  },
];

function TypingDots() {
  return (
    <div className="flex justify-start">
      <div className="px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5 bg-[oklch(0.52_0.09_128/0.08)] border border-[oklch(0.52_0.09_128/0.15)]">
        <div className="w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-bounce" style={{ animationDelay: "0ms", animationDuration: "0.8s" }} />
        <div className="w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-bounce" style={{ animationDelay: "150ms", animationDuration: "0.8s" }} />
        <div className="w-1.5 h-1.5 rounded-full bg-[oklch(0.52_0.09_128)] animate-bounce" style={{ animationDelay: "300ms", animationDuration: "0.8s" }} />
      </div>
    </div>
  );
}

function SparkleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="inline-block mr-1">
      <path d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" fill="oklch(0.52 0.09 128)" opacity="0.7" />
    </svg>
  );
}

function DiaryBubble({ msg, visible }: { msg: DiaryMessage; visible: boolean }) {
  if (msg.type === "date") {
    return (
      <div
        className="text-center py-2 transition-all duration-500"
        style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(8px)" }}
      >
        <div className="text-[12px] font-semibold text-[oklch(0.52_0.09_128)]">{msg.text}</div>
        {msg.sublabel && <div className="text-[10px] text-[oklch(0.5_0.02_80)]">{msg.sublabel}</div>}
      </div>
    );
  }

  if (msg.type === "label") {
    return (
      <div
        className="flex justify-end transition-all duration-500"
        style={{ opacity: visible ? 1 : 0 }}
      >
        <span className="text-[10px] text-[oklch(0.5_0.02_80)] italic">{msg.text}</span>
      </div>
    );
  }

  if (msg.type === "user") {
    return (
      <div
        className="flex justify-start transition-all duration-500"
        style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.95)" }}
      >
        <div className="max-w-[88%] px-3.5 py-2.5 rounded-2xl rounded-bl-md text-[11.5px] leading-[1.55] bg-[oklch(0.96_0.005_80)] text-[oklch(0.3_0.02_80)] border border-[oklch(0.9_0.01_80)]">
          {msg.text}
        </div>
      </div>
    );
  }

  // Bot / Reflexion message
  return (
    <div
      className="flex justify-start transition-all duration-500"
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.95)" }}
    >
      <div className="max-w-[88%] px-3.5 py-2.5 rounded-2xl rounded-bl-md text-[11.5px] leading-[1.55] bg-[oklch(0.52_0.09_128/0.08)] text-[oklch(0.3_0.02_80)] border border-[oklch(0.52_0.09_128/0.15)]">
        {msg.label && (
          <div className="flex items-center gap-1 mb-1.5">
            <SparkleIcon />
            <span className="text-[oklch(0.52_0.09_128)] text-[9px] font-bold tracking-wider uppercase">{msg.label}</span>
          </div>
        )}
        <span>{msg.text}</span>
      </div>
    </div>
  );
}

function DiaryPhoneMockup() {
  const { t } = useI18n();
  const [visibleCount, setVisibleCount] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, []);

  const runAnimation = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setVisibleCount(0);
    setShowTyping(false);

    let currentIndex = 0;

    const showNext = () => {
      if (currentIndex >= diarySequence.length) {
        setShowTyping(true);
        setTimeout(scrollToBottom, 100);
        setTimeout(() => {
          setIsAnimating(false);
          setVisibleCount(0);
          setShowTyping(false);
          setTimeout(() => runAnimation(), 2000);
        }, 6000);
        return;
      }

      const msg = diarySequence[currentIndex];
      const isBot = msg.type === "bot";
      const isDate = msg.type === "date";
      const isLabel = msg.type === "label";

      if (isBot) {
        setShowTyping(true);
        setTimeout(scrollToBottom, 50);
        const typingDuration = Math.min(900 + (msg.text?.length || 0) * 10, 2500);
        setTimeout(() => {
          setShowTyping(false);
          currentIndex++;
          setVisibleCount(currentIndex);
          setTimeout(scrollToBottom, 50);
          setTimeout(showNext, 500);
        }, typingDuration);
      } else if (isDate || isLabel) {
        currentIndex++;
        setVisibleCount(currentIndex);
        setTimeout(scrollToBottom, 50);
        setTimeout(showNext, 600);
      } else {
        currentIndex++;
        setVisibleCount(currentIndex);
        setTimeout(scrollToBottom, 50);
        setTimeout(showNext, 900);
      }
    };

    setTimeout(showNext, 800);
  }, [isAnimating, scrollToBottom]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStarted.current) {
            hasStarted.current = true;
            setTimeout(() => runAnimation(), 500);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [runAnimation]);

  return (
    <div className="relative mx-auto w-[320px] max-w-full" ref={sectionRef}>
      {/* Phone Frame - Light theme */}
      <div
        className="relative rounded-[44px] overflow-hidden"
        style={{
          background: "linear-gradient(145deg, #e8e8e8 0%, #d0d0d0 50%, #c0c0c0 100%)",
          padding: "10px",
          boxShadow: "0 25px 60px rgba(0,0,0,0.15), 0 10px 20px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)",
        }}
      >
        <div
          className="relative rounded-[36px] overflow-hidden"
          style={{ background: "#ffffff", height: "620px" }}
        >
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-3 pb-1 relative">
            <span className="text-[11px] font-semibold text-[oklch(0.3_0.02_80)]">9:41</span>
            <div
              className="w-[90px] h-[28px] bg-black rounded-full"
              style={{ position: "absolute", top: "10px", left: "50%", transform: "translateX(-50%)" }}
            />
            <div className="flex items-center gap-1">
              <svg width="14" height="10" viewBox="0 0 14 10" fill="oklch(0.3 0.02 80)" opacity="0.8">
                <rect x="0" y="6" width="2.5" height="4" rx="0.5" />
                <rect x="3.5" y="4" width="2.5" height="6" rx="0.5" />
                <rect x="7" y="2" width="2.5" height="8" rx="0.5" />
                <rect x="10.5" y="0" width="2.5" height="10" rx="0.5" />
              </svg>
              <svg width="14" height="10" viewBox="0 0 24 16" fill="oklch(0.3 0.02 80)" opacity="0.8">
                <path d="M1 5.5C4.5 1.5 9 0 12 0s7.5 1.5 11 5.5l-2 2C18 4 15 2.5 12 2.5S6 4 3 7.5l-2-2z" />
                <path d="M5 9.5C7 7 9.5 6 12 6s5 1 7 3.5l-2 2C15.5 9.5 14 9 12 9s-3.5.5-5 2.5l-2-2z" />
                <circle cx="12" cy="14" r="2" />
              </svg>
              <svg width="22" height="10" viewBox="0 0 25 11" fill="none" stroke="oklch(0.3 0.02 80)" opacity="0.8">
                <rect x="0.5" y="0.5" width="20" height="10" rx="2" strokeWidth="1" />
                <rect x="22" y="3" width="2" height="5" rx="1" fill="oklch(0.3 0.02 80)" opacity="0.4" />
                <rect x="1.5" y="1.5" width="14" height="8" rx="1" fill="oklch(0.52 0.09 128)" opacity="0.8" />
              </svg>
            </div>
          </div>

          {/* Chat Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[oklch(0.9_0.01_80)]">
            <div className="flex items-center gap-2">
              <span className="text-[oklch(0.4_0.02_80)] text-[18px]">‹</span>
            </div>
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: "linear-gradient(135deg, oklch(0.52 0.09 128), oklch(0.45 0.09 128))" }}>
              <PenLine size={14} className="text-white" />
            </div>
            <div>
              <div className="text-[oklch(0.2_0.02_80)] text-[13px] font-semibold leading-tight">{t("landing.tagebuch.chatHeader")}</div>
              <div className="text-[oklch(0.52_0.09_128)] text-[10px] leading-tight">{t("landing.tagebuch.chatSubheader")}</div>
            </div>
            <div className="ml-auto w-2 h-2 rounded-full bg-[oklch(0.52_0.09_128)] animate-pulse" />
          </div>

          {/* Chat Messages */}
          <div
            ref={chatContainerRef}
            className="px-3 py-4 space-y-3 overflow-y-auto"
            style={{
              height: "calc(100% - 135px)",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            <style>{`.diary-scroll::-webkit-scrollbar { display: none; }`}</style>

            {diarySequence.map((msg, i) => (
              <DiaryBubble key={i} msg={msg} visible={i < visibleCount} />
            ))}

            {showTyping && <TypingDots />}

            <div className="h-14" />
          </div>

          {/* Input bar */}
          <div className="absolute bottom-0 left-0 right-0 px-3 pb-6 pt-3" style={{ background: "linear-gradient(to top, #ffffff 70%, transparent)" }}>
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-[oklch(0.88_0.01_80)]" style={{ background: "oklch(0.97 0.005 80)" }}>
              <span className="text-[oklch(0.6_0.02_80)] text-[12px] flex-1">{t("landing.tagebuch.inputPlaceholder")}</span>
              <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "oklch(0.52 0.09 128)" }}>
                <PenLine size={13} className="text-white" />
              </div>
            </div>
            <div className="w-[120px] h-[4px] bg-[oklch(0.3_0.02_80/0.2)] rounded-full mx-auto mt-3" />
          </div>
        </div>
      </div>

      {/* Shadow under phone */}
      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[70%] h-6 bg-black/10 blur-xl rounded-full" />
    </div>
  );
}

export default function TagebuchSection() {
  const { t } = useI18n();
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="tagebuch" className="py-24 lg:py-32 relative overflow-hidden" style={{ background: "oklch(0.97 0.005 80)" }} ref={ref}>
      <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <PenLine size={16} className="text-[oklch(0.52_0.09_128)]" />
            <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)]" style={{ fontFamily: "var(--font-body)" }}>
              {t("landing.tagebuch.badge")}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[oklch(0.2_0.02_80)]">
            {t("landing.tagebuch.heading1")} <span className="text-[oklch(0.52_0.09_128)]">{t("landing.tagebuch.heading2")}</span>
          </h2>
          <p className="text-lg text-[oklch(0.45_0.02_80)] mt-6 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
            {t("landing.tagebuch.description")}
          </p>
        </div>

        {/* Content Grid: Phone left, Features right */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Phone Mockup */}
          <div
            className={`transition-all duration-700 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <div className="relative flex justify-center">
              <DiaryPhoneMockup />
            </div>
          </div>

          {/* Right: Features */}
          <div
            className={`transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <div className="space-y-8">
              {diaryFeatures.map((feature, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-5 transition-all duration-500 ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  }`}
                  style={{ transitionDelay: `${i * 150 + 400}ms` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-[oklch(0.52_0.09_128/0.1)] flex items-center justify-center shrink-0 mt-1">
                    <feature.icon size={22} className="text-[oklch(0.52_0.09_128)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[oklch(0.2_0.02_80)] mb-1">{t(feature.titleKey as any)}</h3>
                    <p className="text-[oklch(0.45_0.02_80)] leading-relaxed text-[15px]" style={{ fontFamily: "var(--font-body)" }}>
                      {t(feature.descriptionKey as any)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="mt-10">
              <a
                href="/funnel"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-base transition-all duration-300 hover:scale-105 hover:shadow-lg"
                style={{
                  background: "oklch(0.22 0.02 280)",
                  fontFamily: "var(--font-body)",
                }}
              >
                {t("landing.tagebuch.ctaButton")}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
